import { useCallback, useState } from 'react';
import useChatDragDrop from '../../common/chat/composer/useChatDragDrop';
import { buildChatDropPlan } from '../../common/chat/composer/chatDropPlan';
import { sendFile, sendImages } from '../../common/chat/shared/chatApi';
import { GROUP_CHAT_MAX_FILE_BYTES } from './artistGroupChatPaneUtils';

export default function useArtistGroupChatFileDrop({
  artistUserId,
  chatDropScopeRef,
  handleComposerSent,
  messagesContainerRef,
  readOnly,
  replyDraft,
  thread,
  threadId,
  toastError,
}) {
  const [uploadingDropFiles, setUploadingDropFiles] = useState(false);

  const handleDropFiles = useCallback(
    async (files = [], { rejectedCount = 0 } = {}) => {
      if (!threadId) {
        toastError('チャットを選択してください');
        return;
      }
      if (readOnly || thread?.status !== 'open') {
        toastError('現在このチャットでは送信できません');
        return;
      }
      const plan = buildChatDropPlan(files, { rejectedCount, maxBytes: GROUP_CHAT_MAX_FILE_BYTES });
      if (plan.notice) toastError(plan.notice);
      if (!plan.hasSendable) return;

      setUploadingDropFiles(true);
      try {
        if (plan.images.length > 0) {
          await sendImages({
            mode: 'artist',
            scope: 'group',
            groupThreadId: threadId,
            files: plan.images,
            senderId: artistUserId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        for (const file of plan.files) {
          // eslint-disable-next-line no-await-in-loop -- 送信順を保つため直列に送る
          await sendFile({
            mode: 'artist',
            scope: 'group',
            groupThreadId: threadId,
            file,
            senderId: artistUserId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        await handleComposerSent();
      } catch (error) {
        toastError(error?.message || 'ファイル送信に失敗しました');
      } finally {
        setUploadingDropFiles(false);
      }
    },
    [artistUserId, handleComposerSent, readOnly, replyDraft, thread?.status, threadId, toastError]
  );

  return useChatDragDrop({
    areaRef: messagesContainerRef,
    highlightRef: chatDropScopeRef,
    enabled: Boolean(threadId && thread?.status === 'open' && !readOnly && !uploadingDropFiles),
    onDrop: handleDropFiles,
    onInvalid: (message) => toastError(message || 'この操作は利用できません'),
    maxBytes: GROUP_CHAT_MAX_FILE_BYTES,
  });
}
